import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';

// -- MUI
import { Box, Typography, Link, Paper, Stack, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import MuiTextField from '@mui/material/TextField';
import MuiButton from '@mui/material/Button';
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';

// -- Custom Components
import Logo from '../../general/navBar/Logo';
import Language from './Language';

// -- Routes & Redux
import { requestAuthCode, resendAuthCode, verifyAuthCode } from '../../../routes/general/authentication';
import { setNotificationMessage } from '../../../redux/features/general/notification';
import { setAccountType, setAccountInfo } from '../../../redux/features/general/meta';
import { setGroupAccessList } from '../../../redux/features/auth/login';
import { setIsDeletedAccountPopupOpened } from '../../../redux/features/general/navigation';
import formatAuthCode from '../../../util/auth/formatAuthCode';

// -- Styled Components
const Card = styled(Paper)(({ theme }) => ({
    width: '100%',
    maxWidth: 400,
    padding: theme.spacing(4, 5), // Reduced vertical padding
    borderRadius: 16,
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
}));

const Field = styled(MuiTextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    backgroundColor: '#fff',
    '& input': { 
        color: '#1a1a1a', 
        '::placeholder': { color: '#9e9e9e', opacity: 1 },
        textAlign: 'center',
    },
    '& fieldset': { borderColor: '#d0d0d0' },
    '&:hover fieldset': { borderColor: theme.palette.primary.main },
    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main, borderWidth: 2 },
  },
}));

const PrimaryButton = styled(MuiButton)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 24,
  height: 46,
  boxShadow: theme.shadows[2],
  '&:hover': { boxShadow: theme.shadows[4] },
  '&:focus-visible': { outline: `3px solid ${theme.palette.primary.light}`, outlineOffset: 2 },
}));

const Auth: React.FC = () => {
    const dispatch = useAppDispatch(); 
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { groupAccessId, consultationNoteId } = useParams(); 
    const colors = useAppSelector(state => state.theme.colors); 

    const textMain = colors?.text || '#222';
    const textGrey = colors?.textSecondary || '#5f6368';
    const primaryClr = colors?.primary || '#0B69FF';

    const [email, setEmail] = useState(''); 
    const [verificationCode, setVerificationCode] = useState(''); 
    const [isCodeSent, setIsCodeSent] = useState(false); 
    const [loginAttemptId, setLoginAttemptId] = useState(''); 
    
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(false); 
    const [errorMessage, setErrorMessage] = useState(''); 
    
    const [showResendCode, setShowResendCode] = useState(false); 
    const [allowSubmit, setAllowSubmit] = useState(false); 

    const goToSupport = () => navigate('/hacker/login');

    const handleLogin = async () => {
        if (busy) return;
        setBusy(true);
        setError(false);
        setErrorMessage('');

        try {
            if (!isCodeSent) {
                const res = await requestAuthCode(email, 'doctor') as any;
                if (res.data.status === 200) {
                    setLoginAttemptId(res.data.loginAttemptId);
                    setIsCodeSent(true);
                    dispatch(setNotificationMessage(t('authCodeSent')));
                } else if (res.data.status === 401 && res.data.error === 'deletedAccount') {
                    dispatch(setIsDeletedAccountPopupOpened(true));
                    dispatch(setNotificationMessage(t('invalidEmail')));
                    setError(true);
                } else {
                    throw new Error(res.data.error || 'request_code_failed');
                }
            } else {
                const config = { email, verificationCode, userType: 'doctor', loginAttemptId, groupAccessId, device: isMobile ? 'mobile' : 'desktop' };
                const res = await verifyAuthCode(config) as any;

                if (res.data.status === 200 || res.data.status === 202) {
                    localStorage.setItem('isScreenLocked', 'off');
                    const { token } = res.data;
                    document.cookie = `jwt=${token}; path=/`;
                    dispatch(setAccountType(res.data.userType));
                    dispatch(setAccountInfo(res.data.account));

                    if (res.data.status === 202) {
                        dispatch(setGroupAccessList(res.data.groupAccessList));
                        navigate('/login/group/auth/select');
                    } else if (consultationNoteId) {
                        navigate(`/consultation/note/mic/${consultationNoteId}`);
                    } else {
                        navigate('/dashboard');
                    }
                } else {
                    throw new Error(res.data.error || 'verify_code_failed');
                }
            }
        } catch (e: any) {
            const errorKey = e.message;
            let msg = t('invalidEmail');
            if (isCodeSent) {
                msg = errorKey === 'accountBlocked' ? t('accountBlocked') : t('verificationCodeNotFound');
            }
            dispatch(setNotificationMessage(msg));
            setErrorMessage(msg);
            setError(true);
        } finally {
            setBusy(false);
            setShowResendCode(true);
        }
    };

    const handleResendCode = async () => {
        if (busy) return;
        setBusy(true);
        setError(false);
        setErrorMessage('');

        try {
            const res = await resendAuthCode(email, 'doctor', loginAttemptId) as any;
            if (res.data.status === 200) {
                dispatch(setNotificationMessage(t('authCodeSent')));
            } else {
                throw new Error(res.data.error || 'resend_failed');
            }
        } catch (e: any) {
            const msg = e.message === 'accountBlocked' ? t('accountBlocked') : t('verificationCodeNotFound');
            dispatch(setNotificationMessage(msg));
            setErrorMessage(msg);
            setError(true);
        } finally {
            setBusy(false);
        }
    };

    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (allowSubmit) handleLogin();
    };

    const goBackToEmail = () => {
        setIsCodeSent(false);
        setVerificationCode('');
        setError(false);
        setErrorMessage('');
    };

    useEffect(() => {
        setAllowSubmit(email.length > 3 && email.includes('@'));
    }, [email]);

    useEffect(() => {
        setShowResendCode(false);
    }, [email, verificationCode]);

    useEffect(() => {
        if (isCodeSent && verificationCode?.toString().length === 6) {
            handleLogin();
        }
    }, [verificationCode, isCodeSent]);

    return (
        <Card>
            <Logo size={40} />

            <Typography variant="h6" sx={{ mt: 2, fontWeight: 600, color: textMain, textAlign: 'center' }}>
                {t('loginHeadline')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: textGrey, textAlign: 'center' }}>
                {t('loginTagline')}
            </Typography>

            <Box component="form" onSubmit={handleFormSubmit} sx={{ width: '100%', px: 3 }}>
                <Stack spacing={2}>
                    {isCodeSent && (
                        <Link component="button" type="button" onClick={goBackToEmail} sx={{ display: 'flex', alignItems: 'center', color: textGrey, textDecoration: 'none', mb: -1, alignSelf: 'center' }}>
                            <ArrowCircleLeftIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                            <Typography variant="caption">{t('backToEmail')}</Typography>
                        </Link>
                    )}

                    {!isCodeSent ? (
                        <Field placeholder={String(t('email'))} value={email} onChange={(e) => setEmail(e.target.value)} error={error} helperText={errorMessage} disabled={busy} fullWidth autoFocus />
                    ) : (
                        <Field placeholder="••••••" value={verificationCode} onChange={(e) => setVerificationCode(formatAuthCode(e.target.value))} error={error} helperText={errorMessage} disabled={busy} fullWidth autoFocus inputProps={{ maxLength: 6, style: { letterSpacing: '0.5em' } }} />
                    )}

                    <PrimaryButton type="submit" variant="contained" fullWidth disabled={!allowSubmit || busy} sx={{ backgroundColor: primaryClr, mt: 1 }}>
                        {busy && <CircularProgress size={22} sx={{ position: 'absolute', color: '#fff' }} />}
                        <Box component="span" sx={{ visibility: busy ? 'hidden' : 'visible' }}>
                            {t(isCodeSent ? 'loginButton' : 'sendCode')}
                        </Box>
                    </PrimaryButton>

                    {showResendCode && isCodeSent && !busy && (
                        <Typography variant="body2" sx={{ textAlign: 'center', mt: 1 }}>
                            <Link component="button" type="button" onClick={handleResendCode} sx={{ color: primaryClr }}>
                                {t('resendCode')}
                            </Link>
                        </Typography>
                    )}
                </Stack>
            </Box>

            <Typography variant="body2" sx={{ mt: 3, color: textGrey }}>
                {t('dontHaveAccount')} 
                <Link component="button" type="button" onClick={() => navigate('/signup')} sx={{ fontWeight: 500, color: primaryClr }}>
                    {t('signupButton')}
                </Link>
            </Typography>

            <Typography variant="body2" sx={{ mt: 1.5, color: textGrey }}>
                {t('needHelp')} 
                <Link component="button" type="button" onClick={goToSupport} sx={{ fontWeight: 500, color: primaryClr }}>
                    {t('support')}
                </Link>
            </Typography>

            <Language />
        </Card>
    );
};

export default Auth;